'use client';

import { HomeIcon, PiggyBankIcon, RotateCcwIcon } from 'lucide-react';

import { Provider } from '@/lib/providers/types';
import { Route } from '@/utils/constants';
import {
	ArrowLeftStartOnRectangleIcon,
	BookOpenIcon,
	BuildingOffice2Icon,
	DocumentChartBarIcon,
	InformationCircleIcon,
	PresentationChartLineIcon,
	StarIcon,
	SwatchIcon,
	UserIcon,
} from '@heroicons/react/24/outline';
import { Session } from 'next-auth';
import { signIn, signOut } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { DashboardCommandPalette } from './search';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from './ui/sidebar';
import { UpgradeToPro } from './upgradeToPro';

type HeroIcon = typeof PresentationChartLineIcon;

interface NavigationItem {
	name?: string;
	icon?: HeroIcon;
	href?: string;
	onClick?: () => void;
	className?: string;
	divider?: boolean;
}

interface AppSidebarProps {
	providers: Provider[];
	userSession: Session | null;
	userIsAdminOfProvider: boolean;
}

export function AppSidebar(props: AppSidebarProps) {
	const pathname = usePathname();

	function getIsActive(item: NavigationItem) {
		if (pathname === '/' && item.name === 'Home') return true;
		if (item.name === 'Home') return false;
		return typeof item.href === 'string' && item.href !== '' && pathname.startsWith(item.href);
	}

	const topNavigation: NavigationItem[] = [
		{ name: 'Home', icon: HomeIcon, href: Route.capaPerformance },
		{ name: 'Providers', icon: PresentationChartLineIcon, href: Route.providers },
		{ name: 'Compare', icon: SwatchIcon, href: Route.compare },
		{ name: 'Create Report', icon: DocumentChartBarIcon, href: Route.report },
		{ name: 'Research Library', icon: BookOpenIcon, href: Route.research },
		{ name: 'Consumers', icon: PiggyBankIcon, href: Route.consumers },
		{ name: 'About', icon: InformationCircleIcon, href: Route.about },
		{ name: 'Why CAPAdata', icon: StarIcon, href: Route.why },
	];

	if (props.userSession?.user?.isAdmin === true) {
		topNavigation.push(
			{ divider: true },
			{ name: 'Admin - Users', href: Route.adminUsers, icon: UserIcon },
			{ name: 'Admin - Providers', href: Route.adminProviders, icon: BuildingOffice2Icon },
			{
				name: 'Admin - Reset Intro Box',
				icon: RotateCcwIcon,
				onClick: () => {
					document.cookie = 'userType=; path=/; max-age=0';
					sessionStorage.removeItem('userTypePrompted');
					window.location.href = '/';
				},
			},
		);
	}

	if (props.userSession?.user != null) {
		topNavigation.push(
			{ divider: true },
			{ name: 'Account', icon: UserIcon, href: Route.account },
			{
				name: 'Logout',
				icon: ArrowLeftStartOnRectangleIcon,
				onClick: () => signOut({ callbackUrl: '/auth/signin' }),
				className: 'text-red-600 hover:text-red-700',
			},
		);
	} else {
		topNavigation.push(
			{ divider: true },
			{
				name: 'Log in / Register',
				icon: UserIcon,
				onClick: () => signIn(),
				className: 'text-blue-600 hover:text-blue-700',
			},
		);
	}

	return (
		<Sidebar>
			<SidebarHeader>
				<div className='flex h-14 items-center border-b px-4 lg:h-[60px] lg:px-6'>
					<Link href='/'>
						<Image src='/logo.png' alt='CAPAdata logo' width={200} height={200} />
					</Link>
				</div>
				<DashboardCommandPalette providers={props.providers} />
			</SidebarHeader>

			<SidebarContent className='p-2'>
				<SidebarMenu>
					{topNavigation.map((item, index) =>
						item.divider === true
							? (
								<hr
									key={`divider-${index}`}
									className='my-2 border-t border-gray-200 dark:border-gray-700'
								/>
							)
							: (
								<SidebarMenuItem key={item.name ?? `item-${index}`}>
									{typeof item.href === 'string' && item.href.trim() !== ''
										? (
											<SidebarMenuButton asChild isActive={getIsActive(item)}>
												<a
													href={item.href}
													className={`flex items-center gap-2 w-full ${
														getIsActive(item) ? 'font-semibold text-foreground' : 'text-muted-foreground'
													} ${item.className ?? ''}`}
												>
													{item.icon && <item.icon className='h-5 w-5' />}
													{item.name}
												</a>
											</SidebarMenuButton>
										)
										: (
											<SidebarMenuButton asChild>
												<button
													onClick={item.onClick}
													className={`flex items-center gap-2 w-full text-left text-muted-foreground hover:text-foreground ${
														item.className ?? ''
													}`}
												>
													{item.icon && <item.icon className='h-5 w-5' />}
													{item.name}
												</button>
											</SidebarMenuButton>
										)}
								</SidebarMenuItem>
							)
					)}
				</SidebarMenu>
			</SidebarContent>

			<div className='px-4 pb-2 pt-2 text-xs text-muted-foreground flex gap-2'>
				<Link
					href='/legal/terms'
					className={`hover:text-foreground capitalize ${
						pathname === '/legal/terms' ? 'font-semibold text-foreground' : ''
					}`}
				>
					Terms &amp; Conditions
				</Link>
				<span className='text-gray-400'>|</span>
				<Link
					href='/legal/privacy'
					className={`hover:text-foreground capitalize ${
						pathname === '/legal/privacy' ? 'font-semibold text-foreground' : ''
					}`}
				>
					Privacy Policy
				</Link>
			</div>

			<SidebarFooter>
				{!pathname.startsWith('/upgrade')
					&& !pathname.startsWith('/auth')
					&& !pathname.startsWith('/account') && (
					<UpgradeToPro
						userSubscriptionType={props.userSession?.user?.subscriptionType ?? 'FREE'}
					/>
				)}
			</SidebarFooter>
		</Sidebar>
	);
}
